import React, { useCallback, useState } from 'react'
import { Button, Panel, Tag, Toggle, Loader, IconButton, Modal } from 'rsuite'
import { useFetchAllBanners } from '@@partner:graphql/query/banners/allBanners.gql'
import { useMutateSaveBanner } from '@@partner:graphql/mutation/banners/saveBanner.gql'
import { Banner } from '@@partner:types:app'
import { NO_PHOTO_IMAGE } from '@@partner:config/image'
import { BANNER } from '@hive/status'
import { notifyOk, notifyError } from '@@partner:helpers/notification'
import PlusIcon from '@rsuite/icons/Plus'
import EditIcon from '@rsuite/icons/Edit'
import BannerForm from '../../banners/BannerForm'

type BannerGalleryEditorProps = {
  bannerTypeCode: string
  bannerTypeName?: string
  onClose: () => void
}

export default function BannerGalleryEditor ({
  bannerTypeCode,
  bannerTypeName,
  onClose
}: BannerGalleryEditorProps) {
  const [ showAddBanner, setShowAddBanner ] = useState(false)
  const [ editingBanner, setEditingBanner ] = useState<Banner | null>(null)

  const { data, loading, refetch } = useFetchAllBanners({
    filter: {
      fields: [ 'code' ],
      op: 'eq',
      value: [ bannerTypeCode ]
    },
    pager: { limit: 100 },
    sort: {
      fieldName: 'created_at',
      direction: -1
    }
  })

  const [ saveBanner, { loading: saving } ] = useMutateSaveBanner()

  const handleToggleBanner = useCallback(async (banner: Banner, enabled: boolean) => {
    try {
      await saveBanner({
        variables: {
          newData: {
            id: banner.id,
            name: banner.name,
            file_id: banner.file_id,
            status: enabled ? BANNER.ENABLED : BANNER.DISABLED,
            optionOpenInNewWindow: banner.optionOpenInNewWindow ?? false,
            optionOpenInBrowser: banner.optionOpenInBrowser ?? false
          },
          upsert: false
        }
      })
      notifyOk(enabled ? 'Баннер включен' : 'Баннер отключен')
      refetch()
    }
    catch (error) {
      notifyError('Ошибка изменения статуса баннера')
    }
  }, [ saveBanner, refetch ])

  const handleBannerSaved = useCallback(() => {
    setShowAddBanner(false)
    refetch()
    notifyOk('Баннер добавлен')
  }, [ refetch ])

  const handleBannerEdited = useCallback(() => {
    setEditingBanner(null)
    refetch()
    notifyOk('Баннер сохранён')
  }, [ refetch ])

  const banners = data?.allBanners?.edges ?? []

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: 16,
        padding: '8px 0',
        borderBottom: '1px solid #e5e5e5'
      }}>
        <h4 style={{ margin: 0 }}>
          Баннеры галереи {bannerTypeName && `"${bannerTypeName}"`}
        </h4>
        <div>
          <Button 
            appearance="primary" 
            size="sm"
            startIcon={<PlusIcon />}
            onClick={() => setShowAddBanner(true)}
          >
            Добавить баннер
          </Button>
          <Button 
            appearance="subtle" 
            size="sm" 
            onClick={onClose}
            style={{ marginLeft: 8 }}
          >
            Закрыть
          </Button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Loader size="md" content="Загрузка баннеров..." />
        </div>
      ) : banners.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          Нет баннеров в этой галерее
        </div>
      ) : (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: 12 
        }}>
          {banners.map((banner) => (
            <BannerCard
              key={banner.id}
              banner={banner}
              saving={saving}
              onToggle={handleToggleBanner}
              onEdit={setEditingBanner}
            />
          ))}
        </div>
      )}

      <Modal
        open={showAddBanner}
        onClose={() => setShowAddBanner(false)}
        size="full"
      >
        <Modal.Header>
          <Modal.Title>Добавить баннер</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <BannerForm 
            onSaved={handleBannerSaved}
          />
        </Modal.Body>
      </Modal>

      <Modal
        open={!!editingBanner}
        onClose={() => setEditingBanner(null)}
        size="full"
      >
        <Modal.Header>
          <Modal.Title>Редактировать баннер</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingBanner && (
            <BannerForm 
              bannerId={editingBanner.id}
              onSaved={handleBannerEdited}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  )
}

type BannerCardProps = {
  banner: Banner
  saving: boolean
  onToggle: (banner: Banner, enabled: boolean) => void
  onEdit: (banner: Banner) => void
}

function BannerCard ({ banner, saving, onToggle, onEdit }: BannerCardProps) {
  const isEnabled = banner.status === BANNER.ENABLED

  return (
    <Panel
      bordered
      shaded
      style={{
        opacity: isEnabled ? 1 : 0.6,
        transition: 'opacity 0.2s'
      }}
    >
      <div style={{ marginBottom: 8, position: 'relative' }}>
        <img
          src={banner.bannerSlide?.url ?? NO_PHOTO_IMAGE}
          alt={banner.name}
          style={{
            width: '100%',
            height: 100,
            objectFit: 'cover',
            borderRadius: 4
          }}
        />
        <IconButton
          icon={<EditIcon />}
          size="xs"
          appearance="primary"
          style={{
            position: 'absolute',
            top: 4,
            right: 4
          }}
          onClick={() => onEdit(banner)}
        />
      </div>
      <div style={{ 
        fontSize: 12, 
        fontWeight: 'bold',
        marginBottom: 8,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}>
        {banner.name}
      </div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Tag color={isEnabled ? 'green' : 'red'} size="sm">
          {isEnabled ? 'Активен' : 'Выключен'}
        </Tag>
        <Toggle
          checked={isEnabled}
          loading={saving}
          size="sm"
          onChange={(checked) => onToggle(banner, checked)}
        />
      </div>
    </Panel>
  )
}
